import state from './state';
import store from '@/store';
import getters from './getters';
import mutations from './mutations';

import { IActions } from '@/types/store/i-actions';
import { Entity, PostData, PostDataWithIdsAsStrings } from '@/types/postData';
import { EntityDetails, EntityTypes } from '@/types/entities';
import { Countries } from '@/types/locations';
import { RemainingRouteNames } from '@/types/i-router';
import { ShortcutTypes } from '@/types/shortcut';
import { LoadingStates } from '@/types/i-loading';
import { nonReferralPricingProducts } from '@/static/details-of-specific-pricing-products';

import {
	GeotarginatorFrontCMAClient,
	SaveCampaignGeotargetingRequest,
	FlatEntityConfig,
	groupEntityConfigs,
	EntityConfigsGroup
} from '@studyportals/campaign-management-api-interface';

import EntityDetailsHandler from '@/plugins/entity-details-handler';
import PricingHandler from '@/plugins/pricing-handler';
import LoadingHandler from '@/plugins/loading-handler';
import CookieHandler from '@/plugins/cookie-handler';
import campaignDataHandler from '@/plugins/campaign-data-handler';
import startUpHandler from '@/plugins/start-up-handler';

const actions: IActions = {
	getCampaignData: async (isListingOverview) => {
		// If pricing products are already loaded, all the next steps can be skipped.
		if (state.pricingProducts.length > 0) {
			return;
		}

		if (isListingOverview) {
			const apiUrl = `${state.apiUrl}/campaign/${state.campaignId}`;
			const response = await fetch(apiUrl);
			if (!response.ok) {
				throw new Error('Something went wrong while retrieving country targeting data from the Geo back-end.');
			}

			mutations.setCampaignData(await response.json() as PostDataWithIdsAsStrings);
		} else {
			await actions.retrieveEntitiesAndPricingProducts();
		}

		actions.getEntityDetails(isListingOverview);
		mutations.setLoadingState(LoadingStates.INACTIVE);
	},
	getEntityDetails: (isListingOverview: boolean) => {
		if (!state.postData) {
			return;
		}
		let entityDetails: EntityDetails[];
		if (isListingOverview) {
			entityDetails = EntityDetailsHandler.prepareListingOverviewDetailsForAllEntities(state.postData);
		} else {
			entityDetails = EntityDetailsHandler.prepareDetailsForAllEntities(
				state.enrichedStudyEntityMap,
				state.enrichedOrganisationEntityMap
			);
		}

		mutations.setDetailsOfAllEntities(entityDetails);
	},
	getEntityData: async (type) => {
		const apiUrl = `${state.apiUrl}/${type}?${type}_ids=${state[`${type}Id`]}`;

		const response = await fetch(apiUrl);
		if (!response.ok) {
			throw new Error('Something went wrong while retrieving entity data from the geo back-end.');
		}

		const result = await response.json();
		if (!result.length) {
			return;
		}

		state.campaignId = result[0].campaign_id;

		await actions.getCampaignData(true);
	},
	setCampaignData: async () => {
		try {
			LoadingHandler.beginDisplayingSavingProgress();

			const postData: PostData = state.postData;

			// Build changed entity config groups by comparing current pricing with initial snapshot.
			const changedEntityConfigs = actions.buildChangedEntityConfigs(
				postData.studies,
				EntityTypes.STUDY
			).concat(
				actions.buildChangedEntityConfigs(
					postData.organisations,
					EntityTypes.ORGANISATION
				)
			);

			// groupEntityConfigs groups flat configs by entity; also add groups for entities
			// whose pricing was removed (empty productIDs).
			const changedEntityConfigGroups = groupEntityConfigs(changedEntityConfigs).concat(
				actions.buildRemovedPricingGroups(postData.studies, EntityTypes.STUDY),
				actions.buildRemovedPricingGroups(postData.organisations, EntityTypes.ORGANISATION)
			);

			const request: SaveCampaignGeotargetingRequest = {
				studies: postData.studies.map(entity => ({
					id: entity.id,
					targeted_countries: {
						sp: entity.targeted_countries.sp,
						p: entity.targeted_countries.p,
						lp: entity.targeted_countries.lp
					}
				})),
				organisations: postData.organisations.map(entity => ({
					id: entity.id,
					targeted_countries: {
						sp: entity.targeted_countries.sp,
						p: entity.targeted_countries.p,
						lp: entity.targeted_countries.lp
					}
				})),
				changedEntityConfigGroups
			};

			const tokenId = CookieHandler.retrieveTokenIdCookie();
			const client = new GeotarginatorFrontCMAClient(state.campaignManagementAPIUrl);
			await client.saveCampaignGeotargeting(postData.campaign_id, request, tokenId);
			// No separate audit call — CMA handles auditing internally.
			// eslint-disable-next-line no-useless-catch
		} catch (e) {
			throw e;
		} finally {
			LoadingHandler.wrapUpSavingProgressDisplayProcess();
		}
	},
	buildChangedEntityConfigs: (entities: Entity[], entityType: EntityTypes): FlatEntityConfig[] => {
		const snapshot = state.initialEntityPricingSnapshot;
		const flatConfigs: FlatEntityConfig[] = [];

		for (const entity of entities) {
			const snapshotKey = `${entity.id}-${entityType}`;
			const initialPricing = snapshot[snapshotKey];
			const currentPricing = entity.pricing;

			if (initialPricing === currentPricing) {
				continue;
			}

			// Entity pricing was added or modified (not removed — removed is handled separately).
			if (!currentPricing) {
				continue;
			}

			// Include nonReferralPricingProducts alongside the referral product.
			// TODO (CITA-24471): Evaluate whether nonReferralPricingProducts should be added server-side by CMA
			// instead of being sent by the front-end. Align with team on this.
			for (const nonReferralProductId of nonReferralPricingProducts) {
				flatConfigs.push({
					entityID: entity.id,
					productID: Number(nonReferralProductId),
					entityType
				});
			}
			flatConfigs.push({
				entityID: entity.id,
				productID: Number(currentPricing),
				entityType
			});
		}

		return flatConfigs;
	},
	buildRemovedPricingGroups: (entities: Entity[], entityType: EntityTypes): EntityConfigsGroup[] => {
		const snapshot = state.initialEntityPricingSnapshot;
		const removedGroups: EntityConfigsGroup[] = [];

		for (const entity of entities) {
			const snapshotKey = `${entity.id}-${entityType}`;
			const initialPricing = snapshot[snapshotKey];

			if (initialPricing && !entity.pricing) {
				removedGroups.push({
					entityID: entity.id,
					entityType,
					productIDs: []
				});
			}
		}

		return removedGroups;
	},
	retrieveEntitiesAndPricingProducts: async () => {
		const retrievedEntities = await campaignDataHandler.retrieveCampaignEntities();
		await actions.retrieveAllPricingProducts();

		if (!retrievedEntities) {
			return;
		}

		store.mutations.setEnrichedStudyEntityMap(retrievedEntities.studies);
		store.mutations.setEnrichedOrganisationEntityMap(retrievedEntities.organisations);
	},
	retrieveAllPricingProducts: async () => {
		// eslint-disable-next-line no-useless-catch
		try {
			const products = await PricingHandler.retrieveAllPricingProducts();
			const rawAttachedPricingProductsData = await PricingHandler.retrieveRawAttachedPricingProductsData();
			const campaignStudyConfigItems = PricingHandler.attachPricingProductsForEntities(
				rawAttachedPricingProductsData,
				state.postData.studies,
				EntityTypes.STUDY
			);
			const campaignOrganisationConfigItems = PricingHandler.attachPricingProductsForEntities(
				rawAttachedPricingProductsData,
				state.postData.organisations,
				EntityTypes.ORGANISATION
			);

			const campaignEntityConfigItems = campaignStudyConfigItems.concat(campaignOrganisationConfigItems);
			mutations.setCampaignEntityConfigItems(campaignEntityConfigItems);
			mutations.updateCampaignData(state.postData);
			mutations.setPricingProducts(products);

			// Snapshot initial pricing state for change tracking during save.
			const snapshot: Record<string, string | undefined> = {};
			for (const study of state.postData.studies) {
				snapshot[`${study.id}-${EntityTypes.STUDY}`] = study.pricing;
			}
			for (const org of state.postData.organisations) {
				snapshot[`${org.id}-${EntityTypes.ORGANISATION}`] = org.pricing;
			}
			mutations.setInitialEntityPricingSnapshot(snapshot);
		} catch (e) {
			throw e;
		}
	},
	setItemsChecked: (data) => {
		const { type, checked, targetId } = data;
		const countryList: Countries = state.countries;
		let countryIds: string[] = Object.keys(countryList);

		/* eslint-disable indent */
		switch (type) {
			case ShortcutTypes.REGION:
				countryIds = countryIds.filter((x) => countryList[x].regions.includes(targetId));
				break;
			case ShortcutTypes.CONTINENT:
				countryIds = countryIds.filter((x) => countryList[x].continents.includes(targetId));
				break;
			case ShortcutTypes.FILTER:
				countryIds = getters.filteredCountries();
				break;
		}
		/* eslint-enable indent */

		countryIds.forEach((id: string) => {
			mutations.setCountryChecked({ checked, id });
		});
	},
	setCheckedToPremiumLevel: (level) => {
		const ids = getters.checkedCountryIds();
		ids.forEach((id) => {
			mutations.setCountryPremium({ id, level });
			mutations.setCountryChecked({ checked: false, id });
		});
	},
	resetToDefault: () => {
		mutations.setSelectedContinent(1);
		mutations.setCurrentRoute(RemainingRouteNames.DEFAULT);
		mutations.setPricingProducts([]);
		mutations.setCampaignEntityConfigItems([]);
		mutations.setDetailsOfAllEntities([]);
		mutations.markAllEntitiesAsNotSelected();
		mutations.resetPostData();
		mutations.resetCountryPremiumsForSelectedEntities();
		const ids = getters.checkedCountryIds();
		ids.forEach((id) =>
			mutations.setCountryChecked({
				id,
				checked: false
			})
		);

		startUpHandler.handleRestartActions();
	}
};

export default actions;
